'use client';
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { LuTruck, LuCheck } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { scrollToTop } from '../../utils/scrollUtils';
import {
  GUTTER, PANEL_GAP, PANEL_PAD, SURFACE, CARD_SHADOW,
  DotGrid, Eyebrow, SectionTitle, PillButton, OutlinedNumeral,
} from './landingUi';

// Keys under home.text.points, in display order.
const POINTS = ['advice', 'supplied', 'convenient'];

/**
 * "Persönlich für Sie da": the heading and a "shop from home" photo card on
 * the left, what to expect on the right as numbered cards — the same
 * outlined numerals as the bestseller ranks and the value cards above.
 *
 * Only rendered in the desktop tree of Home.
 */
const Text = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const prefersReduced = useReducedMotion();
  const go = (path) => { navigate(path); setTimeout(() => scrollToTop(), 100); };

  return (
    <section className={`${GUTTER} ${PANEL_GAP}`} aria-label="Pharmacy Information">
      <div className={`relative rounded-[2.5rem] ring-1 ring-black/[0.04] ${PANEL_PAD}`} style={{ backgroundColor: SURFACE }}>
        <DotGrid />

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-[clamp(2rem,4vw,5rem)]">
          {/* Heading, then the shop-from-home photo that fills the column
              beside the paragraph cards. (It used to be sticky over empty
              space; with the photo it is tall enough to scroll normally.) */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div>
              <Eyebrow>{t('banner.pharmacyName')}</Eyebrow>
              <SectionTitle
                className="mt-5"
                lead={t('home.text.title')}
                highlight={t('home.text.titleHighlight')}
              />
              <p className="mt-6 max-w-md text-[15px] leading-relaxed text-gray-500 xl:text-lg">
                {t('home.text.subtitle')}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <PillButton onClick={() => go('/aboutus')}>{t('nav.aboutUs')}</PillButton>
                <PillButton variant="outline" arrow={false} onClick={() => go('/services')}>
                  {t('nav.company.services')}
                </PillButton>
              </div>

              {/* Shop from home — photo card with a caption and a small
                  "order on its way" card floating off it. */}
              <motion.figure
                className="relative mt-10"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="relative h-[24rem] overflow-hidden rounded-[2rem] shadow-[0_34px_70px_-40px_rgba(17,24,39,0.65)] ring-4 ring-white xl:h-[28rem]">
                  <img
                    src="/images/home/shop-from-home.jpg"
                    alt={t('home.text.homeCaption')}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover object-[50%_35%] transition-transform duration-[1.2s] ease-out hover:scale-[1.03]"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1f1210]/75 via-[#1f1210]/10 to-transparent" />

                  <figcaption className="absolute inset-x-0 bottom-0 p-6">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white ring-1 ring-inset ring-white/25 backdrop-blur-sm">
                      <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden="true" />
                      {t('home.text.homeEyebrow')}
                    </span>
                    <p className="mt-3 max-w-sm text-[clamp(1.4rem,2vw,1.85rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-white">
                      {t('home.text.homeCaption')}
                    </p>
                  </figcaption>
                </div>

                {/* Floating order card, bobbing gently */}
                <motion.div
                  aria-hidden="true"
                  className="absolute -right-3 top-6 flex items-center gap-3 rounded-2xl bg-white py-2.5 pl-2.5 pr-4 shadow-[0_20px_44px_-20px_rgba(17,24,39,0.55)] ring-1 ring-black/[0.05] xl:-right-6"
                  animate={prefersReduced ? undefined : { y: [0, -6, 0] }}
                  transition={prefersReduced ? undefined : { duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#F6F3F0] text-brand">
                    <LuTruck className="h-[1.1rem] w-[1.1rem]" />
                  </span>
                  <span className="leading-tight">
                    <span className="flex items-center gap-1 text-[13px] font-bold text-gray-900">
                      {t('home.text.orderStatus')}
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
                        <LuCheck className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                    </span>
                    <span className="text-[11px] font-medium text-gray-500">{t('home.text.orderStatusSub')}</span>
                  </span>
                </motion.div>
              </motion.figure>
            </div>
          </motion.div>

          {/* What to expect — numbered cards */}
          <div className="flex flex-col justify-center gap-[clamp(0.75rem,1.2vw,1.25rem)] lg:col-span-7">
            {POINTS.map((key, index) => (
              <motion.article
                key={key}
                className={`group grid grid-cols-[clamp(3rem,4.2vw,4rem),1fr] items-start gap-x-[clamp(1.25rem,2vw,2rem)] rounded-[2rem] bg-white p-[clamp(1.5rem,2.4vw,2.5rem)] ring-1 ring-black/[0.04] transition-shadow duration-300 ${CARD_SHADOW} hover:shadow-[0_26px_50px_-28px_rgb(var(--brand)/0.45)]`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: 0.05 * index, ease: [0.22, 1, 0.36, 1] }}
              >
                <OutlinedNumeral n={index + 1} className="text-[clamp(2.25rem,3vw,3rem)] transition-colors duration-300 group-hover:text-brand" />
                <div>
                  <h3 className="text-[clamp(1.2rem,1.5vw,1.45rem)] font-extrabold leading-tight tracking-[-0.03em] text-gray-900">
                    {t(`home.text.points.${key}.title`)}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-gray-600 xl:text-base">
                    {t(`home.text.points.${key}.description`)}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

Text.displayName = 'Text';

export default Text;
